'use client'

import Link from 'next/link'
import { useState, useEffect } from 'react'
import { usePathname } from 'next/navigation'

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const pathname = usePathname()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const navItems = [
    { label: 'HOME',         href: '/' },
    { label: 'ABOUT',        href: '/about/bio' },
    { label: 'BUSINESS',     href: '/business' },
    { label: 'BLOG',         href: '/blog' },
    { label: 'INSPIRATIONS', href: '/inspirations' },
    { label: 'CONTACT',      href: '/contact' },
  ]

  const isActive = (href: string) =>
    href === '/' ? pathname === '/' : pathname.startsWith(href)

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300"
      style={{
        background: scrolled
          ? 'radial-gradient(150% 240% at 100% 0%, rgba(150,72,36,0.42) 0%, rgba(78,40,30,0.24) 22%, rgba(20,14,18,0) 52%), linear-gradient(90deg, rgba(4,6,13,0.97) 0%, rgba(6,7,15,0.97) 45%, rgba(14,11,14,0.97) 100%)'
          : 'radial-gradient(150% 240% at 100% 0%, rgba(155,75,38,0.50) 0%, rgba(82,42,32,0.28) 22%, rgba(22,15,19,0) 52%), linear-gradient(90deg, #04060d 0%, #05070e 45%, #0d0a10 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '72px' }}>

          {/* ── LOGO ── */}
          <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', flexShrink: 0 }}>
            {/* QR icon */}
            <div style={{
              width: '46px', height: '46px', flexShrink: 0,
              background: 'rgba(255,255,255,0.06)',
              border: '1.5px solid rgba(255,255,255,0.16)',
              borderRadius: '6px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="28" height="28" viewBox="0 0 28 28" fill="white">
                {/* TL block */}
                <rect x="1" y="1" width="7" height="7"/>
                <rect x="3" y="3" width="3" height="3" fill="#05070e"/>
                {/* TR block */}
                <rect x="20" y="1" width="7" height="7"/>
                <rect x="22" y="3" width="3" height="3" fill="#05070e"/>
                {/* BL block */}
                <rect x="1" y="20" width="7" height="7"/>
                <rect x="3" y="22" width="3" height="3" fill="#05070e"/>
                {/* dots */}
                <rect x="10" y="1" width="2" height="2"/>
                <rect x="13" y="1" width="2" height="2"/>
                <rect x="16" y="1" width="2" height="2"/>
                <rect x="1" y="10" width="2" height="2"/>
                <rect x="4" y="11" width="4" height="4"/>
                <rect x="10" y="10" width="3" height="2"/>
                <rect x="10" y="13" width="2" height="3"/>
                <rect x="13" y="14" width="3" height="2"/>
                <rect x="20" y="10" width="2" height="3"/>
                <rect x="23" y="10" width="4" height="2"/>
                <rect x="20" y="14" width="2" height="4"/>
                <rect x="23" y="16" width="4" height="4"/>
                <rect x="13" y="20" width="2" height="2"/>
                <rect x="16" y="20" width="2" height="2"/>
                <rect x="13" y="23" width="5" height="4"/>
              </svg>
            </div>
            {/* Text */}
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.35 }}>
              <span style={{ fontSize: '9.5px', fontWeight: 800, color: 'rgba(255,255,255,0.90)', letterSpacing: '0.20em' }}>MOHAMMAD</span>
              <span style={{ fontSize: '9.5px', fontWeight: 800, color: 'rgba(255,255,255,0.90)', letterSpacing: '0.20em' }}>GHASSAN</span>
              <span style={{ fontSize: '9.5px', fontWeight: 800, color: '#f97316',              letterSpacing: '0.20em' }}>AL-RIFAI</span>
            </div>
          </Link>

          {/* ── DESKTOP NAV ── */}
          <div className="hidden md:flex items-center" style={{ gap: '36px' }}>
            {navItems.map((item) => {
              const active = isActive(item.href)
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  style={{
                    fontSize: '11.5px',
                    fontWeight: 700,
                    letterSpacing: '0.10em',
                    textDecoration: 'none',
                    textTransform: 'uppercase',
                    color: active ? '#f97316' : 'rgba(255,255,255,0.72)',
                    transition: 'color 0.15s',
                  }}
                  onMouseEnter={e => { if (!active) (e.target as HTMLElement).style.color = '#f97316' }}
                  onMouseLeave={e => { if (!active) (e.target as HTMLElement).style.color = 'rgba(255,255,255,0.72)' }}
                >
                  {item.label}
                </Link>
              )
            })}
          </div>

          {/* ── MOBILE TOGGLE ── */}
          <button
            className="md:hidden"
            onClick={() => setIsOpen(v => !v)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', color: 'rgba(255,255,255,0.85)' }}
          >
            {isOpen
              ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            }
          </button>
        </div>

        {/* ── MOBILE MENU ── */}
        {isOpen && (
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingBottom: '16px' }}>
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setIsOpen(false)}
                style={{
                  display: 'block', padding: '12px 4px',
                  fontSize: '11px', fontWeight: 700,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  textDecoration: 'none',
                  color: isActive(item.href) ? '#f97316' : 'rgba(255,255,255,0.72)',
                }}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  )
}
